import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, Copy } from "lucide-react";
import { linkitApi, formatApiErrorDetail } from "../api";

export default function LeadsPanel() {
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try { const { data } = await linkitApi.get("/page/leads"); setRows(data); }
    catch { toast.error("Could not load leads"); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await linkitApi.delete(`/page/leads/${id}`); setRows((r) => r.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copy = (email) => { navigator.clipboard.writeText(email); toast.success("Email copied"); };

  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/40" /></div>;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-bold text-white">Leads</h2>
        <p className="text-xs text-white/50">Submissions from any lead form on your page.</p>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.03] p-10 text-center">
          <Mail className="mx-auto mb-2 text-white/30" size={22} />
          <p className="text-sm font-medium text-white/70">No leads yet</p>
          <p className="mt-1 text-xs text-white/40">Add a Contact form block to your page to start capturing messages.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((lead) => (
            <div key={lead.id} data-testid={`lead-row-${lead.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-500/20 text-indigo-300 font-bold">{(lead.name || "?").charAt(0).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-white">{lead.name || "Anonymous"}</p>
                    {lead.form_title ? (
                      <span className="rounded-full bg-indigo-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-300" title="Source form">{lead.form_title}</span>
                    ) : null}
                    <span className="ml-auto text-[11px] text-white/40" title={lead.created_at}>{(lead.created_at || "").slice(0, 10)}</span>
                  </div>
                  {lead.email && (
                    <button onClick={() => copy(lead.email)} className="flex items-center gap-1 truncate text-xs text-indigo-300 hover:text-indigo-200">
                      {lead.email} <Copy size={11} />
                    </button>
                  )}
                  {lead.phone && (
                    <button onClick={() => { navigator.clipboard.writeText(lead.phone); toast.success("Phone copied"); }} className="mt-0.5 flex items-center gap-1 truncate text-xs text-white/60 hover:text-white">
                      {lead.phone} <Copy size={10} />
                    </button>
                  )}
                  {lead.message ? (
                    <>
                      <p className={`mt-1.5 text-xs text-white/70 ${expanded === lead.id ? "" : "line-clamp-2"}`}>{lead.message}</p>
                      {lead.message.length > 140 && (
                        <button onClick={() => setExpanded(expanded === lead.id ? null : lead.id)} className="mt-1 text-[11px] font-semibold text-white/50 hover:text-white">
                          {expanded === lead.id ? "Show less" : "Read more"}
                        </button>
                      )}
                    </>
                  ) : null}
                </div>
                <button onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
